import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { GlobalService } from './global/global.service';
import { Subscription } from 'rxjs';
import { DialogService } from './view-shared/dialogs/dialog.service';
import { ToastService } from './view-shared/dialogs/toast.service';
import {
	RouteConfigLoadEnd,
	RouteConfigLoadStart,
	ResolveEnd,
	Router
} from '@angular/router';
import {
	fadeInOnEnterAnimation,
	fadeOutOnLeaveAnimation
} from 'angular-animations';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MixpanelService } from './mixpanel.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	animations: [
		fadeInOnEnterAnimation({ duration: 300 }),
		fadeOutOnLeaveAnimation({ duration: 150 })
	]
})
export class AppComponent implements OnInit, OnDestroy {
	title: string;
	private subscriptions: Subscription = new Subscription();
	private readonly versionReloadKey = 'app-version-reload';
	constructor(
		private titleService: Title,
		public globalService: GlobalService,
		private router: Router,
		private viewContainerRef: ViewContainerRef,
		private dialogService: DialogService,
		private translate: TranslateService,
		private toastService: ToastService,
		private swUpdate: SwUpdate,
		private mixpanelService: MixpanelService
	) {
		this.checkAppVersion();
		this.titleService.setTitle(this.globalService.stateStore.appTitle);
		this.toastService.setViewContainerRef(this.viewContainerRef);

		if (this.swUpdate.isEnabled) {
			console.log('swUpdate.isEnabled');
			this.swUpdate.versionUpdates
				.pipe(
					filter(
						(evt): evt is VersionReadyEvent =>
							evt.type === 'VERSION_READY'
					)
				)
				.subscribe(() => {
					console.log('swUpdate.triggered');
					const handle = () => {
						if ('caches' in window) {
							caches.keys().then(names => {
								for (let name of names) {
									caches.delete(name);
								}
							});
						}
						window.location.reload();
					};

					this.dialogService.showAlert(
						this.translate.instant(
							'ROOT.ERRORS.HTTP.OLD_VERSION_DETECTED.TITLE'
						),
						this.translate.instant(
							'ROOT.ERRORS.HTTP.OLD_VERSION_DETECTED.CONTENT'
						),
						'default',
						handle
					);
				});
		}
	}

	// Refreshes the app when the running build is older than the deployed one.
	private checkAppVersion(): void {
		const current = $ENV.BUILD_ID;
		console.log('[version] running:', current);

		if ('caches' in window) {
			caches.keys().then(names => console.log('[version] caches:', names));
		}

		if (/localhost/.test(document.location.host)) {
			return;
		}

		fetch('/version.json', { cache: 'no-store' })
			.then(res => (res.ok ? res.json() : null))
			.then(data => {
				const latest = data && data.buildId;
				console.log('[version] latest:', latest);

				if (!latest || !current || latest === current) {
					sessionStorage.removeItem(this.versionReloadKey);
					return;
				}
				// Only refresh once per version, to avoid a reload loop.
				if (sessionStorage.getItem(this.versionReloadKey) === latest) {
					return;
				}
				sessionStorage.setItem(this.versionReloadKey, latest);
				console.warn('[version] stale build — refreshing');
				this.forceFreshReload();
			})
			.catch(() => {});
	}

	// Clears caches, removes the service worker, then reloads.
	private async forceFreshReload(): Promise<void> {
		try {
			if ('caches' in window) {
				const names = await caches.keys();
				await Promise.all(names.map(name => caches.delete(name)));
			}
			if ('serviceWorker' in navigator) {
				const regs = await navigator.serviceWorker.getRegistrations();
				await Promise.all(regs.map(reg => reg.unregister()));
			}
		} finally {
			window.location.reload();
		}
	}

	ngOnInit(): void {
		this.router.events.subscribe(event => {
			const urlRef: string = this.router.url;
			this.trackPageView(event);
			if (event instanceof RouteConfigLoadStart) {
				this.dialogService.showSpinner(urlRef, 'Loading module');
			} else if (event instanceof RouteConfigLoadEnd) {
				this.dialogService.closeSpinner(urlRef);
			}
		});
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	getState(outlet: any) {
		return outlet.activatedRouteData.state;
	}

	trackPageView(event): void {
		if (event instanceof ResolveEnd) {
			this.mixpanelService.track('Page Viewed', {
				'Url Path': event.url
			});
		}
	}
}
